import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      toast.success("Password updated. Please sign in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-4 mb-12">
          <img src="/logo-mark.png" alt="MetrixWorks logo" className="h-16 w-16 bg-white p-1" />
          <span className="font-display text-2xl">MetrixWorks</span>
        </Link>
        <div className="bg-[#0A0A0A] border border-white/10 p-10">
          <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Account recovery</p>
          <h1 className="font-display text-3xl tracking-tight mb-8">Choose a new password</h1>
          {!token ? (
            <p data-testid="reset-missing-token" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">
              This reset link is incomplete. Please use the link from your email or <Link to="/forgot-password" className="underline">request a new one</Link>.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              {error && <p data-testid="reset-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>}
              <input data-testid="reset-password-input" type="password" required minLength={8} placeholder="New password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} autoComplete="new-password" />
              <input data-testid="reset-confirm-input" type="password" required minLength={8} placeholder="Confirm new password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} autoComplete="new-password" />
              <button data-testid="reset-submit-button" disabled={loading} className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}
          <p className="mt-8 text-sm text-white/40">
            <Link to="/login" data-testid="reset-back-to-login" className="text-[#D4AF37] hover:underline">Back to sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
